import { Fragment, useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Button,
  Spinner,
  Switch,
  Select,
  MessageBar,
  MessageBarBody,
  Tag,
} from '@fluentui/react-components';
import { AppTopBar } from '../components/AppTopBar.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { Chip } from '../components/Chip.js';
import { useIsEcosoftStaff } from '../stores/authStore.js';
import {
  getPreciosAuditoria,
  getRecursoPorPais,
  getTiposCambioAplicados,
  type FiltroOrigen,
  type PrecioAuditado,
  type PreciosAuditoria,
  type RecursoPorPais,
  type TipoCambioAplicado,
} from '../api/staffPrecios.js';

/**
 * Auditoría de precios — SOLO staff Ecosoft.
 *
 * De dónde salió cada precio que el sistema puso en un presupuesto. Es la
 * herramienta para distinguir un error de PRECIO de un problema de VOCABULARIO:
 *
 *  · Error de precio: una grúa torre a 124 la hora (dólares tomados como pesos).
 *    Se caza ordenando por precio, con los extremos arriba.
 *  · Problema de vocabulario: la IA pidió "Barra de acero A44-28" y lo pagó a
 *    mercado, cuando el catálogo del cliente tenía "Fierro redondo liso" a un
 *    cuarto del precio. Se ve teniendo los DOS textos y los DOS orígenes juntos.
 *
 * Por eso la columna de origen es concreta y no una etiqueta: para mercado, los
 * comercios que respaldan el número (cemento contra Construrama vale; bentonita
 * contra Walmart es bentonita de jardinería, no la de obra); para catálogo, qué
 * catálogo y con qué clave y descripción. La vista NO juzga proveedores: muestra
 * el dato y el criterio lo pone quien mira.
 *
 * Es vista técnica: acá SÍ va el detalle. Nada de esto se muestra al cliente.
 */

const useStyles = makeStyles({
  page: { minHeight: '100vh', background: 'var(--bg-canvas)' },
  body: { padding: '20px 28px 64px', maxWidth: '1600px', margin: '0 auto' },
  head: { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '18px' },
  controls: {
    display: 'flex', gap: '14px', alignItems: 'flex-end', flexWrap: 'wrap',
    padding: '14px 16px', marginBottom: '10px',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: tokens.borderRadiusMedium,
  },
  ctl: { display: 'flex', flexDirection: 'column', gap: '4px' },
  ctlLabel: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  stats: { display: 'flex', gap: '18px', alignItems: 'center', marginLeft: 'auto' },
  filas: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' },
  filaChips: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  filaLabel: {
    fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3,
    minWidth: '58px',
  },
  // La tabla es ancha a propósito (es auditoría): scrollea en su propio contenedor
  // para que el body de la página nunca scrollee en horizontal.
  tableWrap: {
    overflowX: 'auto',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: tokens.borderRadiusMedium,
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: tokens.fontSizeBase200 },
  th: {
    textAlign: 'left', padding: '9px 10px', whiteSpace: 'nowrap',
    borderBottom: '1px solid var(--border-subtle)',
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightSemibold,
    position: 'sticky', top: 0, background: 'var(--bg-surface)', zIndex: 1,
  },
  td: { padding: '8px 10px', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'top' },
  r: { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  // `overflowWrap: anywhere` porque los pliegos traen descripciones sin espacios
  // ("Sistemacompletodeenergíasolar,comprendemateriales…") que sin esto se salen
  // de la celda y se montan encima de la columna siguiente.
  desc: { minWidth: '210px', maxWidth: '300px', overflowWrap: 'anywhere' },
  ctx: { minWidth: '180px', maxWidth: '260px', color: tokens.colorNeutralForeground3, overflowWrap: 'anywhere' },
  origen: { minWidth: '260px', maxWidth: '360px', overflowWrap: 'anywhere' },
  muted: { color: tokens.colorNeutralForeground3 },
  // "No registrado" es un dato, no un vacío: se dice, en cursiva y apagado, para
  // que no se confunda con una celda que no se llegó a pintar.
  falta: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  // La descripción de un concepto de pliego puede tener 40 líneas. En una lista
  // para cazar precios absurdos hay que poder barrer muchos renglones de un
  // vistazo, así que se recorta a 3 líneas (el detalle completo está en la obra).
  clamp3: {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  sospechoso: { background: 'var(--warn-weak)' },
  fxWrap: { marginTop: '26px' },
  small: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  tiny: { fontSize: tokens.fontSizeBase100, color: tokens.colorNeutralForeground3 },
  // Comercio que respalda un precio de mercado. Se lee siempre, sin abrir nada:
  // es lo primero que delata un precio traído de la góndola equivocada.
  prov: {
    display: 'inline-block',
    padding: '1px 7px',
    marginRight: '4px',
    marginTop: '3px',
    borderRadius: '999px',
    border: '1px solid var(--border-default)',
    background: 'var(--bg-raised)',
    color: 'var(--text-secondary)',
    fontSize: tokens.fontSizeBase100,
    whiteSpace: 'nowrap',
  },
  masProv: {
    padding: '1px 7px', marginTop: '3px',
    border: '1px dashed var(--border-strong)',
    background: 'transparent',
    color: 'var(--accent)',
    borderRadius: '999px',
    fontSize: tokens.fontSizeBase100,
    fontFamily: 'inherit',
    cursor: 'pointer',
  },
  // Etiqueta del catálogo: teal = base oficial (eCostos), violeta = catálogo del
  // cliente. Son los mismos colores semánticos que usa el resto de la app.
  catOficial: { color: 'var(--oficial)', fontWeight: tokens.fontWeightSemibold },
  catPrivado: { color: 'var(--privado)', fontWeight: tokens.fontWeightSemibold },
  claveCat: { fontFamily: 'var(--font-mono)', fontSize: tokens.fontSizeBase100 },
  expandBtn: {
    border: '1px solid var(--border-default)',
    background: 'var(--bg-raised)',
    color: 'var(--text-secondary)',
    borderRadius: tokens.borderRadiusSmall,
    width: '22px', height: '22px', lineHeight: '20px',
    padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: '11px',
  },
  detalle: {
    padding: '14px 16px 18px',
    background: 'var(--bg-raised)',
    borderBottom: '1px solid var(--border-subtle)',
  },
  detalleGrid: {
    display: 'grid', gap: '18px',
    gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
    marginTop: '10px',
  },
  bloque: {
    border: '1px solid var(--border-subtle)',
    borderRadius: tokens.borderRadiusMedium,
    background: 'var(--bg-surface)',
    overflowX: 'auto',
  },
  bloqueTitulo: {
    padding: '8px 10px',
    borderBottom: '1px solid var(--border-subtle)',
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
  },
  pais: { fontWeight: tokens.fontWeightSemibold, whiteSpace: 'nowrap' },
});

function money(v: string | null): string {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fecha(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-MX');
}

/** Etiqueta de país para leer: 'SIN' no es un país, es un dato que falta. */
function etiquetaPais(p: string): string {
  return p === 'SIN' ? 'Sin país' : p;
}

/** Cómo se llegó al número, en una celda: moneda nativa, cambio y su fecha. */
function CeldaMoneda({ r }: { r: PrecioAuditado }) {
  const styles = useStyles();
  if (r.fuente === 'catalogo') {
    return (
      <span className={styles.muted}>
        precio del catálogo, sin conversión
      </span>
    );
  }
  if (r.tipo_cambio != null && r.moneda_nativa) {
    return (
      <span>
        {money(r.precio_nativo)} {r.moneda_nativa} × {Number(r.tipo_cambio).toFixed(4)}
        <br />
        <span className={styles.small}>
          {r.tipo_cambio_fecha ?? '—'} · {r.tipo_cambio_fuente ?? '—'}
          {r.tipo_cambio_viejo ? ' · CAMBIO VIEJO' : ''}
        </span>
      </span>
    );
  }
  if (r.moneda_verificada) return <span className={styles.muted}>{r.moneda_nativa ?? r.obra_moneda ?? '—'} (sin convertir)</span>;
  return <Tag size="extra-small" appearance="brand">moneda sin confirmar</Tag>;
}

/**
 * Los comercios detrás de un precio de mercado. Se muestran los tres primeros
 * sin pedir permiso —al menos uno tiene que leerse siempre— y el resto queda a
 * un clic. No se ordenan ni se filtran: es la lista tal como la guardó la
 * búsqueda.
 */
function Proveedores({ lista }: { lista: string[] | null }) {
  const styles = useStyles();
  const [todos, setTodos] = useState(false);
  if (lista == null) {
    return <span className={styles.falta}>proveedor no registrado</span>;
  }
  if (lista.length === 0) {
    // Los dos estados son DISTINTOS y por eso tienen textos distintos: acá el modelo puso un
    // número de su propio conocimiento y no hay comercio que mostrar si un cliente pregunta de
    // dónde salió; arriba (`lista == null`) hubo una búsqueda y no se guardó con qué comercios.
    // Redacción elegida por Sebastián (2026-08-14): "sin comercio" se leía como "falta el dato".
    return <span className={styles.falta}>sin referencia de comercio</span>;
  }
  const visibles = todos ? lista : lista.slice(0, 3);
  const resto = lista.length - visibles.length;
  return (
    <span>
      {visibles.map((p, i) => <span key={`${p}-${i}`} className={styles.prov}>{p}</span>)}
      {resto > 0 && (
        <button type="button" className={styles.masProv} onClick={() => setTodos(true)}>
          +{resto} más
        </button>
      )}
    </span>
  );
}

/** De dónde salió el precio, concreto: comercio, o catálogo con clave y texto. */
function CeldaOrigen({ r }: { r: PrecioAuditado }) {
  const styles = useStyles();

  if (r.fuente === 'catalogo') {
    const oficial = r.catalogo_kind === 'ecostos' || r.catalogo_kind === 'referencia';
    return (
      <span>
        <span className={oficial ? styles.catOficial : styles.catPrivado}>
          {r.catalogo_nombre ?? 'catálogo no registrado'}
        </span>
        {' '}
        <span className={styles.tiny}>
          {r.catalogo_pais ?? 'país no registrado'}
          {r.catalogo_moneda ? ` · ${r.catalogo_moneda}` : ' · moneda no registrada'}
        </span>
        <br />
        <span className={styles.claveCat}>{r.clave_catalogo ?? 'clave no registrada'}</span>
        {' '}
        {r.descripcion_catalogo ?? <span className={styles.falta}>descripción del catálogo no registrada</span>}
        <br />
        <span className={styles.tiny}>
          {r.unidad_catalogo ? `unidad ${r.unidad_catalogo} · ` : ''}
          {r.score_match != null ? `parecido ${Number(r.score_match).toFixed(2)}` : 'parecido no registrado'}
          {r.incluye_mo ? ' · el precio ya incluye mano de obra' : ''}
        </span>
      </span>
    );
  }

  if (r.fuente === 'costo_horario') {
    return (
      <span>
        Costo horario calculado
        <br />
        <span className={styles.tiny}>
          {r.modelo_costo ?? <span className={styles.falta}>modelo de costo no registrado</span>}
        </span>
      </span>
    );
  }

  return (
    <span>
      <Proveedores lista={r.proveedores} />
      <br />
      <span className={styles.tiny}>
        {r.consulted_at
          ? `consultado ${fecha(r.consulted_at)}`
          : 'fecha de consulta no registrada'}
        {r.nota ? ` · ${r.nota}` : ''}
      </span>
    </span>
  );
}

/**
 * El mismo recurso, país por país. Se abre por renglón y se pide una sola vez.
 *
 * Dos bloques porque son dos preguntas distintas: qué precio de mercado hay
 * GUARDADO para cada país (el caché está indexado por país: un precio de México
 * nunca se reusa en Chile), y con qué precio y origen salió el mismo insumo en
 * las obras que ya se cotizaron — ahí aparece el caso de vocabulario, cuando la
 * misma cosa se resolvió por catálogo en una obra y se pagó a mercado en otra.
 */
function DetalleRecurso({ r }: { r: PrecioAuditado }) {
  const styles = useStyles();
  const [data, setData] = useState<RecursoPorPais | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    void getRecursoPorPais({ descripcion: r.descripcion ?? '', tipo: r.tipo })
      .then((d) => { if (vivo) setData(d); })
      .catch((e) => { if (vivo) setError(e instanceof Error ? e.message : 'No se pudo cargar el detalle.'); });
    return () => { vivo = false; };
  }, [r.descripcion, r.tipo]);

  if (error) return <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>;
  if (!data) return <Spinner size="tiny" label="Buscando este recurso en los demás países…" />;

  // Los parecidos van aparte y debajo: son otro insumo con nombre vecino, y
  // mezclados con los exactos parecen el mismo material a dos precios distintos.
  const exactos = data.mercadoPorPais.filter((m) => m.exacto);
  const parecidos = data.mercadoPorPais.filter((m) => !m.exacto);

  return (
    <div>
      <Text className={styles.small}>
        <strong>{r.descripcion}</strong> — el precio de mercado se guarda por país, así que el
        mismo insumo puede valer distinto en cada uno. Abajo, a la izquierda, lo que hay guardado
        por país; a la derecha, con qué precio y de dónde salió en las obras ya cotizadas.
      </Text>
      <div className={styles.detalleGrid}>
        <div className={styles.bloque}>
          <div className={styles.bloqueTitulo}>Precio de mercado guardado, por país</div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>País</th>
                <th className={styles.th}>Unidad</th>
                <th className={`${styles.th} ${styles.r}`}>Precio</th>
                <th className={styles.th}>Moneda</th>
                <th className={styles.th}>Consultado</th>
                <th className={styles.th}>Proveedores</th>
              </tr>
            </thead>
            <tbody>
              {exactos.length === 0 && (
                <tr><td className={styles.td} colSpan={6}>
                  <span className={styles.falta}>
                    No hay precio de mercado guardado para este recurso en ningún país.
                  </span>
                </td></tr>
              )}
              {exactos.map((m, i) => (
                <tr key={`e-${i}`}>
                  <td className={`${styles.td} ${styles.pais}`}>{m.country.toUpperCase()}</td>
                  <td className={styles.td}>{m.unidad || '—'}</td>
                  <td className={`${styles.td} ${styles.r}`}>{money(m.precio_unitario)}</td>
                  <td className={styles.td}>
                    {m.native_currency ?? m.currency
                      ?? <span className={styles.falta}>no registrada</span>}
                  </td>
                  <td className={styles.td}>{fecha(m.consulted_at)}</td>
                  <td className={`${styles.td} ${styles.origen}`}>
                    <Proveedores lista={m.proveedores} />
                  </td>
                </tr>
              ))}
              {parecidos.length > 0 && (
                <tr>
                  <td className={`${styles.td} ${styles.tiny}`} colSpan={6}>
                    Con nombre parecido (ojo: no es el mismo insumo)
                  </td>
                </tr>
              )}
              {parecidos.map((m, i) => (
                <tr key={`p-${i}`}>
                  <td className={`${styles.td} ${styles.pais}`}>{m.country.toUpperCase()}</td>
                  <td className={styles.td}>{m.unidad || '—'}</td>
                  <td className={`${styles.td} ${styles.r} ${styles.muted}`}>{money(m.precio_unitario)}</td>
                  <td className={`${styles.td} ${styles.muted}`}>
                    {m.native_currency ?? m.currency
                      ?? <span className={styles.falta}>no registrada</span>}
                  </td>
                  <td className={`${styles.td} ${styles.muted}`}>{fecha(m.consulted_at)}</td>
                  <td className={`${styles.td} ${styles.origen}`}>
                    <span className={styles.tiny}>{m.descripcion_original ?? m.descripcion_norm}</span>
                    <br />
                    <Proveedores lista={m.proveedores} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.bloque}>
          <div className={styles.bloqueTitulo}>El mismo recurso en las obras cotizadas</div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>País</th>
                <th className={styles.th}>Obra</th>
                <th className={`${styles.th} ${styles.r}`}>Precio</th>
                <th className={styles.th}>De dónde salió</th>
              </tr>
            </thead>
            <tbody>
              {data.otrasObras.length === 0 ? (
                <tr><td className={styles.td} colSpan={4}>
                  <span className={styles.falta}>Este recurso solo aparece en este renglón.</span>
                </td></tr>
              ) : data.otrasObras.map((o, i) => (
                <tr key={i} className={o.concept_id === r.concept_id && o.precio === r.precio ? styles.sospechoso : undefined}>
                  <td className={`${styles.td} ${styles.pais}`}>{etiquetaPais(o.pais_etiqueta)}</td>
                  <td className={`${styles.td} ${styles.ctx}`}>
                    {o.obra}
                    <div className={styles.tiny}>{o.empresa ?? ''}</div>
                  </td>
                  <td className={`${styles.td} ${styles.r}`}>
                    {money(o.precio)}
                    <div className={styles.tiny}>{o.obra_moneda ?? '—'} · {o.unidad || '—'}</div>
                  </td>
                  <td className={`${styles.td} ${styles.origen}`}><CeldaOrigen r={o} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const ORIGEN_CHIPS: { id: FiltroOrigen; label: string }[] = [
  { id: 'fuera_catalogo', label: 'Fuera del catálogo' },
  { id: 'mercado', label: 'Mercado' },
  { id: 'costo_horario', label: 'Costo horario' },
  { id: 'catalogo', label: 'Catálogo del cliente' },
  { id: 'todos', label: 'Todos' },
];

export function StaffPreciosPage() {
  const styles = useStyles();
  const isStaff = useIsEcosoftStaff();

  const [q, setQ] = useState('');
  const [orden, setOrden] = useState<'precio' | 'precio_asc' | 'fecha'>('precio');
  const [soloSospechosos, setSoloSospechosos] = useState(false);
  const [origen, setOrigen] = useState<FiltroOrigen>('fuera_catalogo');
  const [pais, setPais] = useState<string | null>(null);
  const [abierto, setAbierto] = useState<string | null>(null);
  const [data, setData] = useState<PreciosAuditoria | null>(null);
  const [fx, setFx] = useState<TipoCambioAplicado[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async (texto: string) => {
    setLoading(true);
    setError(null);
    try {
      const r = await getPreciosAuditoria({
        q: texto || undefined, orden, soloSospechosos, origen,
        pais: pais ?? undefined, limit: 300,
      });
      setData(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar la auditoría de precios.');
    } finally {
      setLoading(false);
    }
  }, [orden, soloSospechosos, origen, pais]);

  // El filtro de texto se dispara con un pequeño retardo para no consultar por
  // cada tecla (la consulta recorre todas las matrices de todos los tenants).
  useEffect(() => {
    const t = setTimeout(() => { void cargar(q); }, 350);
    return () => clearTimeout(t);
  }, [q, cargar]);

  useEffect(() => {
    void getTiposCambioAplicados().then((r) => setFx(r.items)).catch(() => setFx([]));
  }, []);

  if (!isStaff) return <Navigate to="/" replace />;

  const porOrigen = (f: string) => data?.origenes.find((o) => o.fuente === f)?.n ?? 0;
  const conteoOrigen = (id: FiltroOrigen): number | null => {
    if (!data) return null;
    if (id === 'todos') return data.origenes.reduce((s, o) => s + o.n, 0);
    if (id === 'fuera_catalogo') return porOrigen('mercado') + porOrigen('costo_horario');
    return porOrigen(id);
  };

  return (
    <div className={styles.page}>
      <AppTopBar />
      <div className={styles.body}>
        <Breadcrumbs items={[{ label: 'Inicio', to: '/' }, { label: 'Auditoría de precios (staff)' }]} />
        <div className={styles.head}>
          <Text size={600} weight="semibold">Auditoría de precios</Text>
          <Text className={styles.small}>
            De dónde salió cada precio: el comercio que respalda una referencia de mercado, o el
            catálogo y la clave cuando el precio ya lo tenía el cliente. Ordena por precio para
            cazar los absurdos, y abre un renglón para ver el mismo recurso país por país.
          </Text>
        </div>

        <div className={styles.controls}>
          <div className={styles.ctl}>
            <span className={styles.ctlLabel}>Buscar (recurso, concepto u obra)</span>
            <Input
              value={q}
              onChange={(_e, d) => setQ(d.value)}
              placeholder="grúa, andamio, válvula…"
              style={{ minWidth: 260 }}
            />
          </div>
          <div className={styles.ctl}>
            <span className={styles.ctlLabel}>Orden</span>
            <Select value={orden} onChange={(_e, d) => setOrden(d.value as typeof orden)}>
              <option value="precio">Precio: más caro primero</option>
              <option value="precio_asc">Precio: más barato primero</option>
              <option value="fecha">Más reciente primero</option>
            </Select>
          </div>
          <div className={styles.ctl}>
            <span className={styles.ctlLabel}>&nbsp;</span>
            <Switch
              checked={soloSospechosos}
              onChange={(_e, d) => setSoloSospechosos(d.checked)}
              label="Solo los sospechosos"
            />
          </div>
          <Button appearance="secondary" onClick={() => void cargar(q)} disabled={loading}>Refrescar</Button>
          {data && (
            <div className={styles.stats}>
              <Text className={styles.small}>{data.total.toLocaleString('es-MX')} renglones</Text>
              {data.sinMonedaConfirmada > 0 && (
                <Tag size="small" appearance="brand">
                  {data.sinMonedaConfirmada.toLocaleString('es-MX')} sin moneda confirmada
                </Tag>
              )}
              {data.bajoElPiso > 0 && (
                <Tag size="small" appearance="brand">
                  {data.bajoElPiso.toLocaleString('es-MX')} por debajo del piso del catálogo
                </Tag>
              )}
            </div>
          )}
        </div>

        <div className={styles.filas}>
          <div className={styles.filaChips}>
            <span className={styles.filaLabel}>Origen</span>
            {ORIGEN_CHIPS.map((o) => (
              <Chip
                key={o.id}
                label={o.label}
                count={conteoOrigen(o.id)}
                active={origen === o.id}
                onClick={() => { setOrigen(o.id); setAbierto(null); }}
              />
            ))}
          </div>
          <div className={styles.filaChips}>
            <span className={styles.filaLabel}>País</span>
            <Chip
              label="Todos"
              count={data ? data.paises.reduce((s, p) => s + p.n, 0) : null}
              active={pais == null}
              onClick={() => { setPais(null); setAbierto(null); }}
            />
            {(data?.paises ?? []).map((p) => (
              <Chip
                key={p.pais}
                label={etiquetaPais(p.pais)}
                count={p.n}
                active={pais === p.pais}
                title={p.pais === 'SIN'
                  ? 'La obra no declaró país: el precio se buscó como si fuera México'
                  : `Obras en ${p.pais}`}
                onClick={() => { setPais(p.pais); setAbierto(null); }}
              />
            ))}
          </div>
        </div>

        {error && (
          <MessageBar intent="error" style={{ marginBottom: 14 }}>
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        {loading && !data ? <Spinner label="Cargando…" /> : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th} aria-label="Ver por país" />
                  <th className={styles.th}>Recurso</th>
                  <th className={styles.th}>Tipo</th>
                  <th className={styles.th}>Unidad</th>
                  <th className={`${styles.th} ${styles.r}`}>Precio</th>
                  <th className={styles.th}>País · moneda</th>
                  <th className={styles.th}>Cómo se llegó al precio</th>
                  <th className={styles.th}>Origen: proveedor o catálogo</th>
                  <th className={`${styles.th} ${styles.r}`}>Valor de adquisición</th>
                  <th className={styles.th}>Obra · concepto</th>
                  <th className={styles.th}>Resuelto</th>
                </tr>
              </thead>
              <tbody>
                {(data?.items ?? []).length === 0 ? (
                  <tr><td className={styles.td} colSpan={11} style={{ textAlign: 'center', fontStyle: 'italic' }}>
                    Sin renglones que mostrar.
                  </td></tr>
                ) : (data?.items ?? []).map((r, i) => {
                  const id = `${r.concept_id}-${i}`;
                  const sospechoso = r.fuente !== 'catalogo' && (!r.moneda_verificada || r.precio_no_confiable);
                  return (
                    <Fragment key={id}>
                      <tr className={sospechoso ? styles.sospechoso : undefined}>
                        <td className={styles.td}>
                          <button
                            type="button"
                            className={styles.expandBtn}
                            title={abierto === id ? 'Cerrar' : 'Ver este recurso país por país'}
                            aria-expanded={abierto === id}
                            onClick={() => setAbierto(abierto === id ? null : id)}
                          >
                            {abierto === id ? '−' : '+'}
                          </button>
                        </td>
                        <td className={`${styles.td} ${styles.desc}`}>
                          {r.descripcion ?? '—'}
                          {r.presentacion ? <><br /><span className={styles.small}>{r.presentacion}</span></> : null}
                          {/* El precio se conservó (nunca cero) — acá se dice por qué hay que mirarlo. */}
                          {r.precio_no_confiable ? (
                            <><br /><span className={styles.small}>⚠ {r.motivo_no_confiable ?? 'por debajo del piso del catálogo'}</span></>
                          ) : null}
                        </td>
                        <td className={styles.td}>{r.tipo === 'equipo' ? 'Equipo' : 'Material'}</td>
                        <td className={styles.td}>{r.unidad || '—'}</td>
                        <td className={`${styles.td} ${styles.r}`}><strong>{money(r.precio)}</strong></td>
                        <td className={styles.td}>
                          <span className={styles.pais}>{r.obra_pais ?? 'sin país'}</span>
                          {' · '}
                          {r.obra_moneda ?? <span className={styles.falta}>sin moneda</span>}
                          {r.obra_pais == null && (
                            <><br /><span className={styles.tiny}>se buscó como {r.pais_busqueda.toUpperCase()}</span></>
                          )}
                        </td>
                        <td className={styles.td}><CeldaMoneda r={r} /></td>
                        <td className={`${styles.td} ${styles.origen}`}><CeldaOrigen r={r} /></td>
                        <td className={`${styles.td} ${styles.r}`}>
                          {r.valor_adquisicion == null ? <span className={styles.muted}>—</span> : (
                            <>
                              {money(r.valor_adquisicion)}
                              {r.va_nativo && r.va_moneda_nativa ? (
                                <><br /><span className={styles.small}>{money(r.va_nativo)} {r.va_moneda_nativa} × {Number(r.va_tipo_cambio ?? 1).toFixed(4)}</span></>
                              ) : null}
                            </>
                          )}
                        </td>
                        <td className={`${styles.td} ${styles.ctx}`}>
                          {r.obra}
                          <div className={`${styles.small} ${styles.clamp3}`} title={r.concepto}>
                            {r.concepto_clave ? `${r.concepto_clave} · ` : ''}{r.concepto}
                          </div>
                        </td>
                        <td className={styles.td}>{fecha(r.generado_en)}</td>
                      </tr>
                      {abierto === id && (
                        <tr>
                          <td className={styles.detalle} colSpan={11}>
                            <DetalleRecurso r={r} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className={styles.fxWrap}>
          <Text size={500} weight="semibold">Tipos de cambio en uso</Text>
          <Text className={styles.small} style={{ display: 'block', marginTop: 4, marginBottom: 10 }}>
            Los cambios que el sistema consultó, con su fecha y de dónde salieron. Nunca hay un tipo
            de cambio escrito en el código: si las fuentes no responden se reusa el último conocido y
            los renglones que lo usaron quedan marcados como calculados con un cambio viejo.
          </Text>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>Par</th>
                  <th className={`${styles.th} ${styles.r}`}>Cambio</th>
                  <th className={styles.th}>Fecha del dato</th>
                  <th className={styles.th}>Fuente</th>
                  <th className={styles.th}>Cruce</th>
                  <th className={styles.th}>Consultado</th>
                </tr>
              </thead>
              <tbody>
                {fx.length === 0 ? (
                  <tr><td className={styles.td} colSpan={6} style={{ textAlign: 'center', fontStyle: 'italic' }}>
                    Todavía no se consultó ningún tipo de cambio.
                  </td></tr>
                ) : fx.map((t, i) => (
                  <tr key={i}>
                    <td className={styles.td}>{t.base_currency} → {t.quote_currency}</td>
                    <td className={`${styles.td} ${styles.r}`}>{Number(t.rate).toFixed(6)}</td>
                    <td className={styles.td}>{t.rate_date?.slice(0, 10) ?? '—'}</td>
                    <td className={styles.td}>{t.source}</td>
                    <td className={styles.td}>{t.pivot_currency ?? <span className={styles.muted}>directo</span>}</td>
                    <td className={styles.td}>{fecha(t.fetched_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
